import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import styles from "./IdProffVeri.module.css";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

/* =========================================================
   API
========================================================= */

const API_URL =
  "https://coinyoco.world/brobizee-api/partnerdata.php";

/* =========================================================
   DOCUMENT TYPES
========================================================= */

const documentTypes = [
  {
    id: "aadhar",
    label: "Aadhar Card",
  },
  {
    id: "pan",
    label: "PAN Card",
  },
  {
    id: "dl",
    label: "Driving License",
  },
];

/* =========================================================
   COMPONENT
========================================================= */

export default function IdProffVeri({
  onBack,
  onSubmit,
}) {
  const navigate = useNavigate();

  const {
    userId,
    deviceUuid,
  } = useParams();

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  const goTo = (path) => {
    navigate(
      basePath
        ? `${basePath}${path}`
        : path
    );
  };

  /* =======================================================
     STORAGE
  ======================================================= */

  const STORAGE_KEY =
    userId && deviceUuid
      ? `partnerProfile_${userId}_${deviceUuid}`
      : "partnerProfile";

  /* =======================================================
     REFS
  ======================================================= */

  const fileInputRef =
    useRef(null);

  const cameraInputRef =
    useRef(null);

  /* =======================================================
     STATE
  ======================================================= */

  const [selectedDoc, setSelectedDoc] =
    useState("aadhar");

  /* uploadedFiles → [{ file, preview, size, name }] */
  const [uploadedFiles, setUploadedFiles] =
    useState([]);

  const [error, setError] =
    useState("");

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  /* Popup / loading state */
  const [isUploading, setIsUploading] =
    useState(false);

  /* =======================================================
     RESTORE DOCUMENT DATA
  ======================================================= */

  useEffect(() => {
    try {
      const saved =
        localStorage.getItem(
          STORAGE_KEY
        );

      if (!saved) {
        return;
      }

      const data = JSON.parse(saved);

      if (data) {
        if (data.document_type) {
          setSelectedDoc(
            data.document_type
          );
        }

        /* 
          File objects cannot be restored from
          localStorage — so we only restore the
          document type (and show saved names
          as informational if needed).
        */
      }
    } catch (err) {
      console.error(
        "Unable to restore documents:",
        err
      );
    }
  }, [STORAGE_KEY]);

  /* =======================================================
     CLEANUP PREVIEW URLs
  ======================================================= */

  useEffect(() => {
    return () => {
      uploadedFiles.forEach((item) => {
        if (
          item.preview &&
          item.preview.startsWith("blob:")
        ) {
          URL.revokeObjectURL(
            item.preview
          );
        }
      });
    };
  }, [uploadedFiles]);

  /* =======================================================
     DOCUMENT SELECT
  ======================================================= */

  const handleDocumentSelect = (docId) => {
    setSelectedDoc(docId);
    setError("");
  };

  /* =======================================================
     FILE UPLOAD (NO COMPRESSION)
  ======================================================= */

  const handleFileUpload = async (e) => {
    const files = Array.from(
      e.target.files || []
    );

    if (!files.length) {
      return;
    }

    /* ------ Limit: sirf 2 files allow ------ */

    const remainingSlots =
      2 - uploadedFiles.length;

    if (remainingSlots <= 0) {
      setError(
        "You can upload only 2 documents (front & back)."
      );

      e.target.value = "";

      return;
    }

    const filesToProcess =
      files.slice(0, remainingSlots);

    /* ------ Validate file types ------ */

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "application/pdf",
    ];

    const validFiles = [];

    for (const f of filesToProcess) {
      if (
        !allowedTypes.includes(f.type) &&
        !f.name
          .toLowerCase()
          .endsWith(".pdf")
      ) {
        setError(
          "Only JPG, PNG, WEBP or PDF files are allowed."
        );

        e.target.value = "";

        return;
      }

      if (
        f.size > 10 * 1024 * 1024
      ) {
        setError(
          "File size must be less than 10 MB."
        );

        e.target.value = "";

        return;
      }

      validFiles.push(f);
    }

    try {
      setIsUploading(true);
      setError("");

      /* ------ Process files (no compression) ------ */

      const processed = [];

      for (const f of validFiles) {
        const previewUrl =
          f.type === "application/pdf"
            ? null
            : URL.createObjectURL(f);

        processed.push({
          file: f,
          preview: previewUrl,
          size: f.size,
          name: f.name,
          isPdf:
            f.type === "application/pdf",
        });

        console.log(
          `Uploaded "${f.name}" →`,
          (f.size / 1024).toFixed(2),
          "KB"
        );
      }

      setUploadedFiles((prev) => [
        ...prev,
        ...processed,
      ]);

      /* Small delay so popup is visible */
      await new Promise((r) =>
        setTimeout(r, 600)
      );
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  /* =======================================================
     REMOVE FILE
  ======================================================= */

  const handleRemoveFile = (
    indexToRemove
  ) => {
    setUploadedFiles((prev) => {
      const removed = prev[indexToRemove];

      if (
        removed?.preview &&
        removed.preview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(
          removed.preview
        );
      }

      return prev.filter(
        (_, index) =>
          index !== indexToRemove
      );
    });

    setError("");
  };

  /* =======================================================
     DOCUMENT VALIDATION (Aadhar / PAN / DL)
  ======================================================= */

  const analyzeImage = (file) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith("image/")) {
        resolve({
          ok: true,
          reason: "",
        });
        return;
      }

      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();

        img.onload = () => {
          try {
            const canvas =
              document.createElement(
                "canvas"
              );

            const ctx =
              canvas.getContext("2d");

            const MAX = 120;

            let w = img.width;
            let h = img.height;

            const scale =
              MAX /
              Math.max(w, h);

            w = Math.round(w * scale);
            h = Math.round(h * scale);

            canvas.width = w;
            canvas.height = h;

            ctx.drawImage(
              img,
              0,
              0,
              w,
              h
            );

            const data =
              ctx.getImageData(
                0,
                0,
                w,
                h
              ).data;

            /* -------- Aspect ratio -------- */

            const aspectRatio =
              img.width / img.height;

            /* -------- Average brightness + variance -------- */

            let sum = 0;
            let sumSq = 0;

            const pixels =
              w * h;

            for (
              let i = 0;
              i < data.length;
              i += 4
            ) {
              const brightness =
                0.299 * data[i] +
                0.587 * data[i + 1] +
                0.114 * data[i + 2];

              sum += brightness;
              sumSq += brightness * brightness;
            }

            const mean = sum / pixels;

            const variance =
              sumSq / pixels -
              mean * mean;

            const stdDev = Math.sqrt(
              Math.max(variance, 0)
            );

            /* -------- Heuristics -------- */

            /* 1. Bahut dark ya bahut light → blur/blank */
            if (mean < 25 || mean > 240) {
              resolve({
                ok: false,
                reason:
                  "Document is too dark or too bright. Please capture again with proper lighting.",
              });
              return;
            }

            /* 2. Bahut low contrast → likely blurry */
            if (stdDev < 12) {
              resolve({
                ok: false,
                reason:
                  "Document looks blurry or blank. Please upload a clearer photo.",
              });
              return;
            }

            /* 3. Aspect ratio check (document zyada square/circle nahi) */
            if (
              aspectRatio < 0.4 ||
              aspectRatio > 3.2
            ) {
              resolve({
                ok: false,
                reason:
                  "Document aspect ratio looks wrong. Please capture the full document clearly.",
              });
              return;
            }

            resolve({
              ok: true,
              reason: "",
            });
          } catch (err) {
            console.error(
              "analyzeImage error:",
              err
            );

            resolve({
              ok: true,
              reason: "",
            });
          }
        };

        img.onerror = () =>
          resolve({
            ok: true,
            reason: "",
          });

        img.src = event.target.result;
      };

      reader.onerror = () =>
        resolve({
          ok: true,
          reason: "",
        });

      reader.readAsDataURL(file);
    });
  };

  /* =======================================================
     VALIDATE FORM (structure + doc hint + blur)
  ======================================================= */

  const validateForm = async () => {
    if (!selectedDoc) {
      setError(
        "Please select a document type."
      );
      return false;
    }

    if (uploadedFiles.length < 2) {
      setError(
        "Please upload both sides of your document (front & back)."
      );
      return false;
    }

    /* -------- Filename hint check -------- */

    const docKeywords = {
      aadhar: [
        "aadhar",
        "aadhaar",
        "uidai",
      ],
      pan: ["pan", "income", "tax"],
      dl: [
        "dl",
        "license",
        "licence",
        "driving",
      ],
    };

    const keywords =
      docKeywords[selectedDoc] || [];

    /* -------- Image analysis (blur / brightness) -------- */

    for (const item of uploadedFiles) {
      if (item.isPdf) continue;

      const result =
        await analyzeImage(item.file);

      if (!result.ok) {
        setError(result.reason);
        return false;
      }
    }

    /* Filename mismatch = warning only, block nahi karenge
       (kyunki user koi bhi naam rakh sakta hai) */

    const hasMatchHint =
      uploadedFiles.some((item) => {
        const lower =
          item.name.toLowerCase();

        return keywords.some((k) =>
          lower.includes(k)
        );
      });

    if (
      keywords.length &&
      !hasMatchHint
    ) {
      console.warn(
        "Filename does not contain typical keywords for this document type. Continuing anyway."
      );
    }

    return true;
  };

  /* =======================================================
     SAVE DOCUMENT DATA
  ======================================================= */

  const saveDocumentData = async () => {
    let previousData = {};

    try {
      const saved =
        localStorage.getItem(
          STORAGE_KEY
        );

      if (saved) {
        previousData =
          JSON.parse(saved) || {};
      }
    } catch (err) {
      console.error(
        "Unable to read partner data:",
        err
      );
    }

    const firstFile =
      uploadedFiles[0];

    const secondFile =
      uploadedFiles[1];

    const dataToSave = {
      ...previousData,

      secondary_id:
        userId ||
        previousData.secondary_id ||
        "",

      secondary_uuid:
        deviceUuid ||
        previousData.secondary_uuid ||
        "",

      document_type: selectedDoc,

      upload_documents_images1:
        firstFile?.name ||
        previousData
          .upload_documents_images1 ||
        "",

      upload_documents_images2:
        secondFile?.name ||
        previousData
          .upload_documents_images2 ||
        "",

      work:
        previousData.work || "",

      experiences:
        previousData.experiences || "",

      full_name:
        previousData.full_name || "",

      phone_number:
        previousData.phone_number || "",

      email_id:
        previousData.email_id || "",

      password:
        previousData.password || "",

      confirm_password:
        previousData.confirm_password ||
        "",

      address:
        previousData.address || "",

      age: previousData.age || "",

      gender:
        previousData.gender || "",

      bio: previousData.bio || "",

      account_type:
        previousData.account_type || "",

      account_holder_name:
        previousData.account_holder_name ||
        "",

      account_number:
        previousData.account_number || "",

      ifsc_code:
        previousData.ifsc_code || "",

      stutes:
        previousData.stutes || "",

      online_stutes:
        previousData.online_stutes || "",

      profile:
        previousData.profile || "",

      profit:
        previousData.profit ?? 0,

      wallet:
        previousData.wallet ?? 0,

      money:
        previousData.money ?? 0,

      invest:
        previousData.invest ?? 0,

      upi_id:
        previousData.upi_id || "",

      refrences:
        previousData.refrences || "",

      recently_work:
        previousData.recently_work || "",

      secondary_work:
        previousData.secondary_work || "",

      helper_list:
        previousData.helper_list || "",
    };

    console.log(
      "Saving document data:",
      dataToSave
    );

    const response = await fetch(
      API_URL,
      {
        method: "POST",
        headers: {
          "Content-Type":
            "application/json",
        },
        body: JSON.stringify(
          dataToSave
        ),
      }
    );

    const responseText =
      await response.text();

    console.log(
      "Document API response:",
      responseText
    );

    let result;

    try {
      result = JSON.parse(responseText);
    } catch {
      throw new Error(
        "PHP API returned invalid JSON."
      );
    }

    if (!response.ok) {
      throw new Error(
        result.message ||
          `Server error: ${response.status}`
      );
    }

    if (!result.success) {
      throw new Error(
        result.message ||
          "Document data could not be saved."
      );
    }

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(dataToSave)
    );

    localStorage.setItem(
      "partnerProfile",
      JSON.stringify(dataToSave)
    );

    if (onSubmit) {
      await onSubmit({
        selectedDoc,
        uploadedFiles,
      });
    }

    return result;
  };

  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!userId || !deviceUuid) {
      setError(
        "User ID or Device UUID is missing."
      );
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      const valid =
        await validateForm();

      if (!valid) return;

      await saveDocumentData();

      goTo("/bankvari");
    } catch (err) {
      console.error(
        "Document submission failed:",
        err
      );

      setError(
        err.message ||
          "Unable to submit documents."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /* =======================================================
     BACK
  ======================================================= */

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    navigate(-1);
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div className={styles.screenWrapper}>
      <div className={styles.deviceFrame}>
        <div className={styles.scrollContainer}>

          {/* =================================================
              UPLOADING POPUP MODAL
          ================================================= */}
          {isUploading && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background:
                  "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 9999,
                backdropFilter:
                  "blur(2px)",
              }}
            >
              <div
                style={{
                  background: "#fff",
                  borderRadius: "16px",
                  padding: "28px 32px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "14px",
                  boxShadow:
                    "0 12px 40px rgba(0,0,0,0.18)",
                  minWidth: "220px",
                }}
              >
                {/* Spinner */}
                <div
                  style={{
                    width: "38px",
                    height: "38px",
                    border:
                      "4px solid #e5e7eb",
                    borderTop:
                      "4px solid #2563eb",
                    borderRadius: "50%",
                    animation:
                      "idproffSpin 0.8s linear infinite",
                  }}
                />

                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#111827",
                    textAlign: "center",
                  }}
                >
                  Uploading documents...
                </p>

                <p
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    color: "#6b7280",
                    textAlign: "center",
                  }}
                >
                  Please wait, don't close this
                  window.
                </p>
              </div>

              {/* Inline keyframes (React-friendly) */}
              <style>
                {`
                  @keyframes idproffSpin {
                    0%   { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          )}

          <header className={styles.header}>
            <button
              type="button"
              className={styles.backButton}
              onClick={handleBack}
              aria-label="Go Back"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>

            <div className={styles.stepIndicator}>
              <span className={styles.step} />
              <span className={styles.step} />
              <span
                className={`${styles.step} ${styles.stepActive}`}
              />
              <span className={styles.step} />
            </div>
          </header>

          <main className={styles.mainContent}>
            <h1 className={styles.title}>
              ID Proof Verification
            </h1>

            <form
              onSubmit={handleFormSubmit}
              className={styles.formContainer}
            >
              <div className={styles.sectionGroup}>
                <label
                  className={styles.sectionLabel}
                >
                  Select type of Document
                </label>

                <div className={styles.documentList}>
                  {documentTypes.map((doc) => {
                    const isSelected =
                      selectedDoc === doc.id;

                    return (
                      <button
                        key={doc.id}
                        type="button"
                        className={`${styles.docCard} ${
                          isSelected
                            ? styles.docCardSelected
                            : ""
                        }`}
                        onClick={() =>
                          handleDocumentSelect(
                            doc.id
                          )
                        }
                      >
                        <span
                          className={
                            styles.docLabel
                          }
                        >
                          {doc.label}
                        </span>

                        <div
                          className={`${styles.radioButton} ${
                            isSelected
                              ? styles.radioSelected
                              : ""
                          }`}
                        >
                          {isSelected && (
                            <div
                              className={
                                styles.radioInnerCircle
                              }
                            />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={styles.sectionGroup}>
                <label
                  className={styles.sectionLabel}
                >
                  Upload Document
                </label>

                <div
                  className={`${styles.uploadDropzone} ${
                    error &&
                    uploadedFiles.length < 2
                      ? styles.uploadError
                      : ""
                  }`}
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                >
                  <div
                    className={
                      styles.uploadHeader
                    }
                  >
                    <button
                      type="button"
                      className={
                        styles.uploadBtnTrigger
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      +<span>Upload</span>
                    </button>
                  </div>

                  <div
                    className={styles.uploadBody}
                  >
                    <p
                      className={
                        styles.uploadHint
                      }
                    >
                      {uploadedFiles.length ===
                      0
                        ? "Upload Image On Both Sides."
                        : uploadedFiles.length ===
                          1
                        ? "1 side uploaded. Please upload the other side."
                        : `${uploadedFiles.length} files uploaded.`}
                    </p>
                  </div>
                </div>

                {/* =================================================
                    PREVIEW GRID — Dono documents dikhenge
                ================================================= */}

                {uploadedFiles.length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(2, 1fr)",
                      gap: "12px",
                      marginTop: "14px",
                    }}
                  >
                    {uploadedFiles.map(
                      (item, index) => (
                        <div
                          key={`${item.name}-${index}`}
                          style={{
                            position:
                              "relative",
                            background:
                              "#f7f8fa",
                            borderRadius:
                              "14px",
                            border:
                              "1px solid #e6e8ec",
                            overflow:
                              "hidden",
                            padding:
                              "8px",
                          }}
                        >
                          <div
                            style={{
                              width:
                                "100%",
                              height:
                                "120px",
                              borderRadius:
                                "10px",
                              overflow:
                                "hidden",
                              background:
                                "#fff",
                              display:
                                "flex",
                              alignItems:
                                "center",
                              justifyContent:
                                "center",
                              marginBottom:
                                "6px",
                            }}
                          >
                            {item.preview ? (
                              <img
                                src={
                                  item.preview
                                }
                                alt={
                                  item.name
                                }
                                style={{
                                  width:
                                    "100%",
                                  height:
                                    "100%",
                                  objectFit:
                                    "cover",
                                }}
                              />
                            ) : (
                              <span
                                style={{
                                  fontSize:
                                    "28px",
                                }}
                              >
                                📄
                              </span>
                            )}
                          </div>

                          <div
                            style={{
                              fontSize:
                                "11px",
                              fontWeight: 600,
                              color:
                                "#222",
                              overflow:
                                "hidden",
                              textOverflow:
                                "ellipsis",
                              whiteSpace:
                                "nowrap",
                            }}
                            title={
                              item.name
                            }
                          >
                            {index + 1}.{" "}
                            {item.name}
                          </div>

                          <div
                            style={{
                              fontSize:
                                "10px",
                              color:
                                "#6b7280",
                              marginTop:
                                "2px",
                            }}
                          >
                            {(
                              item.size /
                              1024
                            ).toFixed(
                              1
                            )}{" "}
                            KB ·{" "}
                            {index === 0
                              ? "Front"
                              : "Back"}
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveFile(
                                index
                              )
                            }
                            style={{
                              position:
                                "absolute",
                              top: "6px",
                              right:
                                "6px",
                              width:
                                "24px",
                              height:
                                "24px",
                              borderRadius:
                                "50%",
                              border:
                                "none",
                              background:
                                "rgba(255,0,0,0.85)",
                              color:
                                "#fff",
                              fontSize:
                                "14px",
                              fontWeight: 700,
                              cursor:
                                "pointer",
                              display:
                                "flex",
                              alignItems:
                                "center",
                              justifyContent:
                                "center",
                            }}
                            aria-label="Remove"
                          >
                            ×
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  multiple
                  accept="image/*,.pdf"
                  className={styles.hiddenInput}
                />

                <input
                  type="file"
                  ref={cameraInputRef}
                  onChange={handleFileUpload}
                  accept="image/*"
                  capture="environment"
                  className={styles.hiddenInput}
                />

                <button
                  type="button"
                  className={
                    styles.cameraTriggerBtn
                  }
                  onClick={() =>
                    cameraInputRef.current?.click()
                  }
                  disabled={isUploading}
                >
                  📷
                  <span>
                    Open Camera
                  </span>
                </button>
              </div>

              {error && (
                <div
                  className={
                    styles.errorMessage
                  }
                  role="alert"
                >
                  <span>{error}</span>
                </div>
              )}

              <div
                className={styles.actionWrapper}
              >
                <button
                  type="submit"
                  className={`${styles.submitBtn} ${
                    uploadedFiles.length < 2 ||
                    isUploading
                      ? styles.submitBtnDisabled
                      : ""
                  }`}
                  disabled={
                    isSubmitting ||
                    isUploading
                  }
                >
                  {isSubmitting
                    ? "Saving..."
                    : isUploading
                    ? "Uploading..."
                    : "Continue"}
                </button>
              </div>
            </form>
          </main>

        </div>
      </div>
    </div>
  );
}